import type { DYAppConfig, DYSeasonalLogo } from "../app.config.value"

/**
 * [2026-06-04] 현재 요청의 host를 반환한다.
 * - 서버: 리버스 프록시 뒤이므로 원본 호스트가 담긴 `x-forwarded-host`를 우선 사용하고, 없으면 `host`.
 *         (`useRequestURL().host`는 기본적으로 `Host` 헤더만 읽어 프록시 업스트림 호스트가 잡히므로 별칭 매칭이 깨진다)
 * - 클라이언트: window.location 기반
 * Nuxt 컨텍스트가 없는 등 예외 상황에서는 undefined를 반환한다.
 * [2026-06-05] x-forwarded-host 우선 처리 추가 — web-mfg.doyac.com SSR에서 host가 myfirstguide로 잡혀
 *              fullDomain/애플 redirect_uri 등이 차단 도메인으로 굳던 문제 수정.
 */
function getCurrentRequestHost(): string | undefined {
  try {
    if (import.meta.server) {
      const headers = useRequestHeaders(['x-forwarded-host', 'host'])
      // x-forwarded-host 는 프록시가 여러 개면 콤마로 누적될 수 있으므로 첫 번째(원본 클라이언트 기준)를 사용
      const forwarded = headers['x-forwarded-host']
      const host = (forwarded ? forwarded.split(',')[0] : headers['host'])?.trim()
      if (host) return host
    }
    return useRequestURL().host
  } catch {
    return undefined
  }
}

export function useDYAppConfig() {
  //@ts-ignore
  const config = useAppConfig() as DYAppConfig
  const result = { ...config }

  // [2026-06-04] 차단 우회용 별칭 도메인 처리
  // 특정 국가/네트워크에서 메인 도메인이 차단될 때, 앱 등에서 별칭 도메인(예: web-mfg.doyac.com)으로
  // 접속하면 domain/fullDomain/fullUrl/API_BASE_URL/STORAGE_BASE_URL 등을 해당 별칭 체계로 덮어쓴다.
  // 이렇게 해야 차단 대상 도메인(예: myfirstguide.com)이 클라이언트에 노출되지 않는다.
  // domainAliases가 설정되지 않은 프로젝트는 host 조회조차 하지 않으므로 기존 동작과 완전히 동일하다.
  const aliases = config.domainAliases
  if (aliases) {
    const host = getCurrentRequestHost()
    if (host && aliases[host]) {
      Object.assign(result, aliases[host])
    }
  }

  return result
}

/**
 * [2026-06-05] 현재 요청이 차단 우회용 별칭 도메인(domainAliases에 등록된 host)으로 들어온 것인지 여부.
 * 별칭 도메인에서는 도메인 1개만 등록 가능한 네이버 로그인 등 일부 외부연동 기능이 제한된다.
 * (예: web-mfg.doyac.com 접속 시 true)
 */
export function useDYIsAliasDomain(): boolean {
  //@ts-ignore
  const config = useAppConfig() as DYAppConfig
  if (!config.domainAliases) return false
  const host = getCurrentRequestHost()
  return !!(host && config.domainAliases[host])
}

/**
 * [2026-08-05] 한국시간(Asia/Seoul) 기준 오늘 날짜를 `'YYYY-MM-DD'` 형식으로 반환한다.
 * 서버(UTC일 수도 있음)와 클라이언트(사용자 로컬 타임존)가 서로 다른 값을 계산하면
 * hydration 불일치가 생기므로, 양쪽 모두 한국시간으로 고정해서 계산한다.
 */
function getTodayInKST(): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  try {
    // 로케일에 따라 출력 순서/구분자가 달라질 수 있어 formatToParts로 안전하게 조립한다
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date())
    const year = parts.find(it => it.type === 'year')?.value
    const month = parts.find(it => it.type === 'month')?.value
    const day = parts.find(it => it.type === 'day')?.value
    if (year && month && day) return `${year}-${month}-${day}`
  } catch {
    // Intl 타임존 데이터가 없는 극단적인 환경 대비
  }
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * 날짜가 시작~종료 범위(양끝 포함)에 들어가는지.
 * `end`가 `start`보다 앞서면 해를 넘기는 기간으로 본다. (예: '12-27' ~ '01-05')
 * 자릿수가 고정된 형식(`'MM-DD'`, `'YYYY-MM-DD'`)이라 문자열 사전순 비교가 곧 날짜순 비교가 된다.
 */
function isDateInRange(today: string, start: string, end: string): boolean {
  if (start <= end) return today >= start && today <= end
  return today >= start || today <= end
}

/**
 * 오늘이 해당 계절 로고의 노출 기간인지 판정한다.
 * - `start`/`end`(`'MM-DD'`)는 매년 반복되는 기간
 * - `dateRanges`(`'YYYY-MM-DD'`)는 설날처럼 연도마다 날짜가 달라지는 기간
 * 둘 다 지정되어 있으면 하나만 맞아도 노출한다.
 */
function isSeasonalLogoActive(logo: DYSeasonalLogo, today: string): boolean {
  if (logo.start && logo.end && isDateInRange(today.slice(5), logo.start, logo.end)) return true
  return !!logo.dateRanges?.some(range => isDateInRange(today, range.start, range.end))
}

/**
 * [2026-08-05] 현재 적용 중인 계절 로고. 경로는 basePath까지 적용된 최종 값이다.
 */
export interface DYResolvedSeasonalLogo {
  name: string
  /** 상단 헤더용 가로형 로고 */
  image: string
  /** 서브페이지 액션바 좌상단 미니 로고. 설정하지 않았으면 undefined */
  miniImage?: string
}

/**
 * [2026-08-05] 계절(기간)별 로고를 반환한다.
 * `appConfig.seasonalLogos`에 등록된 항목 중 현재(한국시간) 월에 해당하는 첫 항목을 사용하며,
 * 해당하는 항목이 없거나 설정 자체가 없으면 `undefined`를 반환한다.
 * (= 호출부에서 기존 기본 로고를 그대로 쓰면 된다)
 *
 * 확인용으로 쿼리스트링을 지원한다.
 * - `?season=summer` : 기간과 상관없이 해당 이름의 로고를 강제 노출
 * - `?season=off`    : 계절 로고를 끄고 기본 로고 노출
 */
export function useDYSeasonalLogo(): DYResolvedSeasonalLogo | undefined {
  const config = useDYAppConfig()
  const seasonalLogos = config.seasonalLogos
  if (!seasonalLogos?.length) return undefined

  let forcedName: string | undefined
  try {
    const value = useRoute().query.season
    forcedName = Array.isArray(value) ? value[0] ?? undefined : value ?? undefined
  } catch {
    // Nuxt 라우터 컨텍스트 밖에서 호출된 경우는 그냥 기간 기준으로 판단
  }
  if (forcedName === 'off') return undefined

  let matched: DYSeasonalLogo | undefined
  if (forcedName) {
    matched = seasonalLogos.find(it => it.name === forcedName)
  } else {
    const today = getTodayInKST()
    matched = seasonalLogos.find(it => isSeasonalLogoActive(it, today))
  }
  if (!matched) return undefined

  // '/'로 시작하는 프로젝트 public 자산은 basePath(`/v` 등)를 붙여야 실제 경로가 된다
  const toUrl = (path: string) => path.startsWith('/') ? `${config.basePath}${path}` : path

  return {
    name: matched.name,
    image: toUrl(matched.image),
    miniImage: matched.miniImage ? toUrl(matched.miniImage) : undefined,
  }
}

/**
 * 인증 관련 쿠키의 도메인을 반환하는 함수.
 * 프로덕션에서는 `.doyac.com` 형태로 반환하여 서브도메인 간 쿠키 공유 및 중복 쿠키 방지.
 * 개발 환경(localhost, IP)에서는 `undefined`를 반환하여 기본 동작 유지.
 */
export function useDYCookieDomain(): string | undefined {
  if (import.meta.dev) return undefined
  const { domain } = useDYAppConfig()
  return `.${domain.replace(/^www\./, '')}`
}